import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const sections = [
  { id: "getting-started",      label: "Getting started" },
  { id: "browsing-search",      label: "Browsing & search" },
  { id: "booking",              label: "Booking a sauna" },
  { id: "fees",                 label: "Fees" },
  { id: "instant-vs-request",   label: "Instant vs request" },
  { id: "booking-statuses",     label: "Booking statuses" },
  { id: "cancelling",           label: "Cancelling" },
  { id: "hosting-listing",      label: "Creating a listing" },
  { id: "hosting-photos",       label: "Photos" },
  { id: "hosting-availability", label: "Availability" },
  { id: "hosting-requests",     label: "Managing requests" },
  { id: "hosting-pausing",      label: "Pausing or removing" },
  { id: "messaging",            label: "Messaging" },
  { id: "profile",              label: "Your profile" },
  { id: "reviews",              label: "Reviews" },
];

const Docs: React.FC = () => {
  usePageTitle("Docs — Lets Go Sauna");
  const [narrow, setNarrow] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div style={pageWrap}>
      <p style={{ color: "#6b7280", marginBottom: 40, fontSize: 15 }}>
        Everything you need to know about browsing, booking, and hosting on Lets Go Sauna.
      </p>

      <div style={{ display: "flex", gap: 48, alignItems: "flex-start", flexDirection: narrow ? "column" : "row" }}>
        {/* TOC */}
        <aside style={{ ...tocWrap, position: narrow ? "static" : "sticky" }}>
          <p style={tocHeading}>On this page</p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {sections.map(s => (
              <li key={s.id}>
                <a href={`#${s.id}`} style={tocLink}>{s.label}</a>
              </li>
            ))}
          </ul>
        </aside>

        {/* Content */}
        <article style={{ flex: 1, minWidth: 0 }}>

          <Section id="getting-started" title="Getting started">
            <p>Create an account with your email address or phone number. Email users sign in with a password; phone users receive a one-time SMS code. After signing up you'll be taken through a quick profile setup — you can skip it, but filling it out pays off when you start booking (see <a href="#profile" style={linkStyle}>Your profile</a>).</p>
            <p>One account works for both guests and hosts. You can book a sauna today and list your own one tomorrow — there's no separate host sign-up.</p>
          </Section>

          <Section id="browsing-search" title="Browsing & search">
            <p>The home page shows recently listed saunas. To search, type any city or address into the search bar — it uses autocomplete to resolve the location — and hit Search. Results appear as a card list on the left and a map on the right; hovering a card highlights its pin.</p>
            <Subheading>Filters</Subheading>
            <ul style={ulStyle}>
              <li><strong>Sauna type</strong> — barrel, cabin, electric, wood-fired, infrared, steam room.</li>
              <li><strong>Amenities</strong> — chips for outdoor shower, cold tub, swimming pool, etc. A listing must have <em>all</em> of the amenities you select, so use these to narrow down rather than cast wide.</li>
              <li><strong>Capacity</strong> — minimum and maximum number of people the sauna fits.</li>
              <li><strong>Price</strong> — per-hour range in dollars.</li>
            </ul>
            <p>The default search radius is 100 miles. Results outside that distance won't appear even if the map pin looks close.</p>
          </Section>

          <Section id="booking" title="Booking a sauna">
            <ol style={olStyle}>
              <li>Open a listing and scroll to the booking widget.</li>
              <li>Pick a date from the calendar. Greyed-out days are fully blocked.</li>
              <li>Choose a start time from the available hourly slots.</li>
              <li>Adjust the duration using the + / − buttons. The widget clamps your selection to the host's set hours and to the next existing booking on that day.</li>
              <li>The total is shown as duration × hourly rate. Click <strong>Book now</strong> or <strong>Request to book</strong> to continue.</li>
            </ol>
            <p>You won't see the booking widget on your own listings.</p>
          </Section>

          <Section id="fees" title="Fees">
            <p>Lets Go Sauna charges a service fee on both sides of a booking:</p>
            <ul style={ulStyle}>
              <li><strong>Guests</strong> pay a 6% service fee on top of the listing's price, added at checkout.</li>
              <li><strong>Hosts</strong> have a 6% fee deducted from their payout for each completed booking.</li>
            </ul>
            <p>The listing's price per hour is always shown before fees; the exact fee amount is itemized at checkout for guests and on the booking detail page for hosts.</p>
          </Section>

          <Section id="instant-vs-request" title="Instant book vs Request to book">
            <p>Each listing is set up by the host in one of two modes:</p>
            <ul style={ulStyle}>
              <li><strong>Instant book</strong> — your booking is confirmed immediately. The calendar blocks the slot and you're good to go.</li>
              <li><strong>Request to book</strong> — the host reviews your request before confirming. Your slot is held while the request is pending so nobody else can take it, but it isn't locked in until the host approves.</li>
            </ul>
            <p>Listings in request mode show a "Host reviews each request" pill on the detail page so you know before you book. Your profile bio, interests, and background help the host decide — a complete profile noticeably improves approval odds.</p>
          </Section>

          <Section id="booking-statuses" title="Booking statuses">
            <ul style={ulStyle}>
              <li><Tag color="#fef3c7" text="#92400e" label="Pending approval" /> — submitted, waiting on the host.</li>
              <li><Tag color="#dbeafe" text="#1e40af" label="Confirmed" /> — approved (or instant-booked). You're all set.</li>
              <li><Tag color="#fee2e2" text="#991b1b" label="Declined" /> — host said no. The slot is freed and you can book elsewhere.</li>
              <li><Tag color="#f3f4f6" text="#374151" label="Cancelled" /> — cancelled by either party before the booking started.</li>
              <li><Tag color="#d1fae5" text="#065f46" label="Completed" /> — the booking window has passed.</li>
            </ul>
            <p>All your bookings (both as guest and host) appear on your Dashboard under "Upcoming Bookings".</p>
          </Section>

          <Section id="cancelling" title="Cancelling">
            <p>Either the guest or the host can cancel a booking while its status is <em>Pending approval</em> or <em>Confirmed</em>. Open the booking detail page and click Cancel. Cancelling a pending request immediately frees the slot.</p>
            <p>There is no penalty mechanism built in — cancellation policy is between you and the host.</p>
          </Section>

          <Divider />
          <h2 style={h2Style}>Hosting</h2>

          <Section id="hosting-listing" title="Creating a listing">
            <p>Go to Dashboard → <strong>Add Sauna</strong>. Fill in:</p>
            <ul style={ulStyle}>
              <li><strong>Name & description</strong> — what makes your sauna worth booking.</li>
              <li><strong>Sauna type</strong> — barrel, cabin, electric, wood-fired, infrared, or steam room.</li>
              <li><strong>Amenities</strong> — check everything that applies. These chips are what guests filter by, so missing an amenity means you won't show up in those searches.</li>
              <li><strong>Address + map pin</strong> — type your address; the map pin auto-fills. Drag the pin to adjust the exact location shown to guests.</li>
              <li><strong>Capacity</strong> — how many people fit comfortably.</li>
              <li><strong>Price per hour</strong> — entered in dollars; stored and displayed accurately.</li>
              <li><strong>Require my approval</strong> — check this if you want to vet each guest before confirming. Leave it unchecked for instant booking.</li>
            </ul>
            <p>After saving you're taken to the Edit page where you can upload photos and set availability.</p>
          </Section>

          <Section id="hosting-photos" title="Photos">
            <p>On your listing's Edit page, upload any number of JPEG, PNG, or WebP photos. Drag them to reorder — the first photo is the one guests see in search results and at the top of your listing. At least one clear photo makes a significant difference in booking rate.</p>
          </Section>

          <Section id="hosting-availability" title="Availability">
            <p>Availability works in two layers:</p>
            <ul style={ulStyle}>
              <li><strong>Weekly rules</strong> — per day of week, set an open and close time. These repeat every week and define when the sauna is theoretically bookable. Disabled days are never shown as available.</li>
              <li><strong>Date blocks</strong> — add one-off blocked dates for holidays, maintenance, or personal use. These override weekly rules for specific days.</li>
            </ul>
            <p>Confirmed and pending bookings also automatically block their slots — you don't need to manage that manually.</p>
          </Section>

          <Section id="hosting-requests" title="Managing requests">
            <p>When a guest requests a booking on an approval-required listing, it appears at the top of your Dashboard under "Pending requests". You'll also see Approve and Decline buttons on the booking's detail page.</p>
            <ul style={ulStyle}>
              <li><strong>Approve</strong> — confirms the booking. The guest is notified and the slot is locked in.</li>
              <li><strong>Decline</strong> — rejects the request and frees the slot. The guest can book elsewhere.</li>
            </ul>
            <p>Before deciding, check the guest's profile. Their bio, interests, hometown, and background appear on the booking detail page.</p>
          </Section>

          <Section id="hosting-pausing" title="Pausing or removing a listing">
            <ul style={ulStyle}>
              <li><strong>Inactive toggle</strong> — on the Dashboard, flip a listing to Inactive to hide it from search results without deleting it. Flip it back when you're ready to accept bookings again.</li>
              <li><strong>Delete</strong> — on the Edit page, delete the listing entirely. This is permanent.</li>
            </ul>
          </Section>

          <Divider />

          <Section id="messaging" title="Messaging">
            <p>A message thread is created automatically for every booking — there's no way to message a host before booking. Open threads from the Messages page or from a booking's detail page via the "Message guest / Message host" link.</p>
            <ul style={ulStyle}>
              <li>Unread threads show a blue dot and highlighted background in the Messages list.</li>
              <li>The Nav shows a red badge with your total unread count.</li>
              <li>Press Enter to send; Shift+Enter for a newline.</li>
              <li>Messages don't update in real time — use the Refresh button to pull new messages.</li>
            </ul>
          </Section>

          <Section id="profile" title="Your profile">
            <p>Fill in your profile from the Profile Setup page (shown after signup) or Settings (reachable from the Nav at any time). Fields: full name, hometown, school, work, interests, bio.</p>
            <p>Your profile is visible to hosts when they review a booking request and to guests on your sauna detail pages as the "Meet your host" card. A thoughtful bio and filled-in background give hosts enough context to approve confidently — incomplete profiles are the most common reason approval-required requests get declined.</p>
          </Section>

          <Section id="reviews" title="Reviews">
            <p>After your booking's check-out time passes, a review form appears directly on the booking detail page. Both the guest and the host are asked to review — the review publishes immediately once submitted.</p>
            <ul style={ulStyle}>
              <li><strong>Guest reviews</strong> rate the listing on Overall, Cleanliness, Communication, Accuracy, and Value (each 1–5 stars), plus an optional public write-up and optional private note to the host.</li>
              <li><strong>Host reviews</strong> rate the guest on Overall, Cleanliness, Communication, and House rules.</li>
              <li>A "Reviews to leave" card at the top of your Dashboard shows any completed stays you haven't reviewed yet — click it to go directly to the form.</li>
              <li>Guest reviews appear on the sauna's listing page with a criteria breakdown, so good reviews improve a host's visibility.</li>
            </ul>
          </Section>

          <div style={{ marginTop: 48, paddingTop: 16, borderTop: "1px solid #e5e7eb", fontSize: 13, color: "#6b7280" }}>
            <Link to="/privacy" style={linkStyle}>Privacy policy</Link>
            {" · "}
            <Link to="/terms" style={linkStyle}>Terms of service</Link>
          </div>

        </article>
      </div>
    </div>
  );
};

const Section: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
    <h3 style={h3Style}>{title}</h3>
    <div style={{ color: "#374151", fontSize: 15, lineHeight: 1.65 }}>{children}</div>
  </section>
);

const Subheading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontWeight: 600, color: "#111827", marginBottom: 6, marginTop: 16 }}>{children}</p>
);

const Divider: React.FC = () => (
  <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "16px 0 24px" }} />
);

const Tag: React.FC<{ color: string; text: string; label: string }> = ({ color, text, label }) => (
  <span style={{
    display: "inline-block", padding: "2px 10px", borderRadius: 12,
    background: color, color: text, fontSize: 13, fontWeight: 600,
  }}>{label}</span>
);

const pageWrap: React.CSSProperties = {
  maxWidth: 960, margin: "40px auto", padding: "0 24px 80px",
};
const h2Style: React.CSSProperties = {
  fontSize: 20, fontWeight: 700, color: "#111827", margin: "0 0 24px",
};
const h3Style: React.CSSProperties = {
  fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 12,
  paddingBottom: 8, borderBottom: "1px solid #f3f4f6",
};
const tocWrap: React.CSSProperties = {
  width: 180, flexShrink: 0, top: 80,
  background: "#f9fafb", border: "1px solid #e5e7eb",
  borderRadius: 8, padding: "16px 16px",
};
const tocHeading: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "#9ca3af",
  textTransform: "uppercase", letterSpacing: "0.06em",
  margin: "0 0 10px",
};
const tocLink: React.CSSProperties = {
  display: "block", padding: "5px 0",
  fontSize: 13, color: "#2563eb", textDecoration: "none", lineHeight: 1.4,
};
const ulStyle: React.CSSProperties = { paddingLeft: 20, margin: "8px 0 12px" };
const olStyle: React.CSSProperties = { paddingLeft: 20, margin: "8px 0 12px" };
const linkStyle: React.CSSProperties = { color: "#2563eb" };

export default Docs;
